import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Car, Bed, Barbell, FirstAidKit, House, Buildings, UsersThree, CalendarBlank,
  Camera, Package, Storefront, ShoppingBag, Bank, TreeStructure, Church, Scissors,
  Martini, ForkKnife, SoccerBall, Lightning, Plus, X, Upload, HandHeart, GraduationCap, MoonStars
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CATEGORIES = [
  { id: 'parking', name: 'Parking', icon: Car },
  { id: 'hotels', name: 'Hotels', icon: Bed },
  { id: 'gyms', name: 'Gyms', icon: Barbell },
  { id: 'yoga_wellness', name: 'Yoga / Wellness', icon: HandHeart },
  { id: 'astrology', name: 'Astrology', icon: MoonStars },
  { id: 'hospital', name: 'Hospital', icon: FirstAidKit },
  { id: 'home', name: 'Home', icon: House },
  { id: 'coworking', name: 'Coworking', icon: Buildings },
  { id: 'meeting_rooms', name: 'Meeting Rooms', icon: UsersThree },
  { id: 'event_spaces', name: 'Events', icon: CalendarBlank },
  { id: 'studios', name: 'Studios', icon: Camera },
  { id: 'storage', name: 'Storage', icon: Package },
  { id: 'shops', name: 'Shops', icon: Storefront },
  { id: 'malls', name: 'Malls', icon: ShoppingBag },
  { id: 'halls', name: 'Halls', icon: Bank },
  { id: 'housing_societies', name: 'Societies', icon: TreeStructure },
  { id: 'religious_places', name: 'Religious', icon: Church },
  { id: 'salons', name: 'Salons', icon: Scissors },
  { id: 'clubs', name: 'Clubs', icon: Martini },
  { id: 'restaurants', name: 'Restaurants', icon: ForkKnife },
  { id: 'sports', name: 'Sports', icon: SoccerBall },
  { id: 'ev_charging', name: 'EV Charging', icon: Lightning },
  { id: 'education', name: 'Education', icon: GraduationCap }
];

export const AMENITIES = [
  'Wi-Fi', 'AC', 'Washroom', 'Security', 'CCTV', 'Parking', 'Drinking Water',
  'Charging Points', 'Lockers', 'Reception', 'Waiting Area', 'Elevator', 'Wheelchair Access'
];

export const PRICING_MODELS = [
  { value: 'hourly', label: 'Per Hour' },
  { value: 'daily', label: 'Per Day' },
  { value: 'weekly', label: 'Per Week' },
  { value: 'monthly', label: 'Per Month' },
  { value: 'per_session', label: 'Per Session' },
  { value: 'per_event', label: 'Per Event' },
];

export const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = String(i).padStart(2, '0');
  const label = i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`;
  return { value: `${h}:00`, label };
});

export const Section = ({ title, children, testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={testId}>
    <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export const CellInput = ({ col, value, onChange, disabled }) => {
  if (col.type === 'toggle') {
    return (
      <div className="flex justify-center">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
          disabled={disabled} className="w-4 h-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500 cursor-pointer disabled:opacity-40"
          data-testid={`cell-${col.key}`} />
      </div>
    );
  }
  if (col.type === 'number') {
    return (
      <input type="number" value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder={col.placeholder || '0'} disabled={disabled}
        className="w-full h-7 px-1.5 border border-slate-200 rounded text-xs bg-white disabled:opacity-40 disabled:bg-slate-50 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
        data-testid={`cell-${col.key}`} />
    );
  }
  if (col.type === 'text') {
    return (
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder={col.placeholder || ''} disabled={disabled}
        className="w-full h-7 px-1.5 border border-slate-200 rounded text-xs bg-white disabled:opacity-40 disabled:bg-slate-50 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
        data-testid={`cell-${col.key}`} />
    );
  }
  if (col.type === 'time') {
    return (
      <input type="time" value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="w-full h-7 px-1 border border-slate-200 rounded text-xs bg-white disabled:opacity-40 disabled:bg-slate-50 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
        data-testid={`cell-${col.key}`} />
    );
  }
  if (col.type === 'select') {
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="w-full h-7 px-1 border border-slate-200 rounded text-xs bg-white disabled:opacity-40 disabled:bg-slate-50 focus:border-emerald-400 outline-none cursor-pointer"
        data-testid={`cell-${col.key}`}>
        <option value="">--</option>
        {col.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }
  return null;
};

export const VariantMediaRow = ({ variantIndex, media, onChange, disabled }) => {
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/seller/upload-media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const newMedia = [...(media || []), { url: res.data.media_url, type: res.data.media_type }];
      onChange(newMedia);
      toast.success('Media uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const addUrlMedia = () => {
    onChange([...(media || []), { url: '', type: 'image' }]);
  };

  const updateMediaItem = (idx, field, val) => {
    const updated = [...(media || [])];
    updated[idx] = { ...updated[idx], [field]: val };
    onChange(updated);
  };

  const removeMedia = (idx) => {
    onChange((media || []).filter((_, i) => i !== idx));
  };

  if (disabled) return null;

  return (
    <div className="mt-1.5 space-y-1.5" data-testid={`variant-media-${variantIndex}`}>
      {(media || []).map((m, idx) => (
        <div key={`vm-${idx}`} className="flex items-center gap-1.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.type === 'video' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
            {m.type === 'video' ? 'VID' : 'IMG'}
          </span>
          <input type="text" value={m.url} onChange={(e) => updateMediaItem(idx, 'url', e.target.value)}
            placeholder="Media URL" className="flex-1 h-6 px-1.5 border border-slate-200 rounded text-[11px] bg-white focus:border-emerald-400 outline-none"
            data-testid={`variant-media-url-${variantIndex}-${idx}`} />
          <select value={m.type} onChange={(e) => updateMediaItem(idx, 'type', e.target.value)}
            className="h-6 px-1 border border-slate-200 rounded text-[11px] bg-white">
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
          <button onClick={() => removeMedia(idx)} className="text-red-400 hover:text-red-600"><X size={12} /></button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <label className={`flex items-center gap-1 px-2 py-1 rounded border border-dashed border-slate-300 text-[11px] text-slate-500 hover:border-emerald-400 hover:text-emerald-600 cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          data-testid={`variant-upload-btn-${variantIndex}`}>
          <Upload size={12} />
          {uploading ? 'Uploading...' : 'Upload'}
          <input type="file" accept="image/*,video/*" onChange={handleFileUpload} className="hidden" />
        </label>
        <button onClick={addUrlMedia} className="flex items-center gap-1 px-2 py-1 rounded border border-dashed border-slate-300 text-[11px] text-slate-500 hover:border-emerald-400 hover:text-emerald-600"
          data-testid={`variant-add-url-btn-${variantIndex}`}>
          <Plus size={12} /> URL
        </button>
      </div>
    </div>
  );
};
